import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import {
  Users, CreditCard, FileText, ChevronRight, AlertTriangle,
  Check, Minus, Plus, Download, Grid3X3, Zap, Clock, Building2, Info, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ManageAccountPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = localStorage.getItem('pulse_token');

  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  // User limit adjustment
  const [newLimit, setNewLimit] = useState(0);
  const [adjusting, setAdjusting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  // Invited users grid
  const [showGrid, setShowGrid] = useState(false);
  const [gridData, setGridData] = useState(null);
  const [gridLoading, setGridLoading] = useState(false);

  // Invoices
  const [showInvoices, setShowInvoices] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Cancel Subscription
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelDetails, setCancelDetails] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Upgrade dialog
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeLimit, setUpgradeLimit] = useState(0);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const fetchSubscription = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/account/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSub(data);
      setNewLimit(data.usage?.paid_users || data.usage?.current_users || 0);
    } catch {
      toast.error('Failed to load account data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  const fetchGrid = async () => {
    setGridLoading(true);
    try {
      const res = await fetch(`${API}/api/account/invited-users-grid`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setGridData(await res.json());
      setShowGrid(true);
    } catch {
      toast.error('Failed to load user grid');
    } finally {
      setGridLoading(false);
    }
  };

  const fetchInvoices = async () => {
    setInvoiceLoading(true);
    try {
      // First sync payment status from Stripe to catch any refunds
      try {
        await fetch(`${API}/api/payments/sync-status`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (syncErr) {
        console.warn('Could not sync payment status:', syncErr);
      }
      
      // Then fetch invoices
      const res = await fetch(`${API}/api/account/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setInvoiceData(await res.json());
      setShowInvoices(true);
    } catch {
      toast.error('Failed to load invoices');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleLimitChange = async () => {
    setAdjusting(true);
    try {
      // If increasing and subscription is active, use pro-rata checkout
      if (newLimit > paidUsers && !isTrial) {
        const res = await fetch(`${API}/api/payments/user-limit-checkout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_limit: newLimit }),
        });
        const data = await res.json();
        if (data.error) {
          toast.error(data.error);
          return;
        }
        if (data.url) {
          toast.info(`Pro-rata charge: $${data.prorata_amount?.toFixed(2)} for ${data.days_remaining} remaining days`);
          window.location.href = data.url;
          return;
        }
      }
      // For decreases or trial users, use direct update
      const res = await fetch(`${API}/api/account/user-limit`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_user_limit: newLimit }),
      });
      const data = await res.json();
      if (data.error) {
        toast.error(data.error);
        return;
      }
      setConfirmData(data);
      setShowConfirm(true);
      fetchSubscription();
    } catch (err) {
      toast.error('Failed to update user limit. Please try again.');
    } finally {
      setAdjusting(false);
    }
  };

  const handleUpgradeCheckout = async () => {
    setUpgradeLoading(true);
    try {
      const res = await fetch(`${API}/api/payments/billing-portal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ requested_users: upgradeLimit }),
      });
      const data = await res.json();
      if (data.error) {
        toast.error(data.error);
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch {
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-400">Loading account...</div>;
  }

  if (!sub) return null;

  const { usage, tier, subscription, pricing, enterprise } = sub;
  const isTrial = subscription?.tier_id === 'free_trial' || subscription?.status === 'trial';
  const isEnterprise = enterprise?.is_enterprise || subscription?.tier_id === 'enterprise';
  const isUnlimited = enterprise?.is_unlimited;
  const currentUsers = usage?.current_users || 0;
  const paidUsers = usage?.paid_users || 0;
  const maxUsers = usage?.max_users || 0;
  const graceLimit = usage?.grace_limit || 0;
  const overGrace = usage?.over_grace;

  // Compute trial days remaining from trial_end date
  const trialDaysRemaining = (() => {
    const trialEnd = subscription?.trial_end || subscription?.trial_ends_at;
    if (!trialEnd) return null;
    const end = new Date(trialEnd);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  })();

  // Graduated pricing tiers (sorted by min_users ascending)
  const tiers = pricing?.tiers?.filter(t => t.id !== 'free_trial' && t.id !== 'enterprise' && t.price_per_user != null)
    .sort((a, b) => (a.min_users || 0) - (b.min_users || 0)) || [];
  
  // Calculate graduated cost
  const calcGraduatedCost = (count) => {
    let cost = 0;
    let remaining = count;
    const breakdown = [];
    for (const t of tiers) {
      const bandCapacity = t.id === 'starter'
        ? (t.max_users || 100)
        : t.max_users
          ? t.max_users - (t.min_users || 0)
          : remaining;
      const usersInBand = Math.min(remaining, bandCapacity);
      if (usersInBand > 0) {
        cost += usersInBand * t.price_per_user;
        breakdown.push({ tier: t.display_name, users: usersInBand, pricePerUser: t.price_per_user, subtotal: usersInBand * t.price_per_user });
        remaining -= usersInBand;
      }
      if (remaining <= 0) break;
    }
    return { cost, breakdown };
  };

  const { cost: monthlyCost, breakdown: costBreakdown } = calcGraduatedCost(newLimit);
  const isDecrease = newLimit < paidUsers;
  const isIncrease = newLimit > paidUsers;

  // Usage bar
  const usagePct = maxUsers > 0 ? Math.min(100, (currentUsers / maxUsers) * 100) : 0;
  const gracePct = maxUsers > 0 ? Math.min(100, (graceLimit / maxUsers) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="manage-account-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Manage Account
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {sub.organization?.name || 'Your Organization'}
        </p>
      </div>

      {/* Trial or Subscription Status */}
      {isTrial ? (
        <Card className="border-amber-200 bg-amber-50" data-testid="trial-status-card">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-900">Free Trial</p>
                <p className="text-sm text-amber-700">
                  {trialDaysRemaining != null ? trialDaysRemaining : '—'} days remaining
                </p>
              </div>
            </div>
            <Button
              onClick={() => {
                setUpgradeLimit(Math.max(currentUsers, 1));
                setShowUpgrade(true);
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="upgrade-from-trial-btn"
            >
              <Zap className="h-4 w-4 mr-2" />
              Upgrade Now
            </Button>
          </CardContent>
        </Card>
      ) : isEnterprise ? (
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50" data-testid="enterprise-status-card">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-purple-600" />
              <div>
                <p className="font-semibold text-purple-900">Enterprise Plan</p>
                <p className="text-sm text-purple-700">
                  {isUnlimited ? 'Unlimited users' : `${maxUsers.toLocaleString()} users`} &middot; Custom pricing
                </p>
              </div>
            </div>
            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
              Enterprise
            </Badge>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="plan-status-card">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-[#5AA2AE]" />
              <div>
                <p className="font-semibold text-slate-900">
                  {tier?.display_name || subscription?.tier_id} Plan
                </p>
                <p className="text-sm text-slate-500">
                  {paidUsers} users &middot; ${sub.pricing?.monthly_cost?.toLocaleString() || (paidUsers * (tier?.price_per_user || 0))}/mo
                </p>
              </div>
            </div>
            <Badge variant={subscription?.status === 'active' ? 'default' : 'secondary'} className="bg-green-100 text-green-700 border-green-200">
              {subscription?.status || 'active'}
            </Badge>
          </CardContent>
        </Card>
      )}

      {/* User Usage */}
      <Card data-testid="user-usage-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-[#5AA2AE]" />
                Invited Users
              </CardTitle>
              <CardDescription className="mt-1">
                {currentUsers} of {maxUsers > 0 ? maxUsers : '—'} users across {usage?.project_count || 0} project{usage?.project_count !== 1 ? 's' : ''}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchGrid} disabled={gridLoading} data-testid="view-users-grid-btn">
              <Grid3X3 className="h-4 w-4 mr-2" />
              {gridLoading ? 'Loading...' : 'View Grid'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Usage bar */}
          {maxUsers > 0 && (
            <div className="space-y-2">
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden relative">
                {/* Grace zone indicator */}
                <div
                  className="absolute top-0 h-full bg-amber-100 rounded-full"
                  style={{ width: `${gracePct}%` }}
                />
                {/* Usage bar */}
                <div
                  className={`absolute top-0 h-full rounded-full transition-all ${
                    overGrace ? 'bg-red-500' : usagePct > 80 ? 'bg-amber-500' : 'bg-[#5AA2AE]'
                  }`}
                  style={{ width: `${usagePct}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-slate-400">
                <span>{currentUsers} invited</span>
                <span>{maxUsers} limit</span>
              </div>
            </div>
          )}

          {/* Overage warning */}
          {overGrace && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700" data-testid="overage-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                You've exceeded the 20% grace overage ({currentUsers} / {maxUsers} users).
                Please increase your user limit below to continue inviting stakeholders.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Adjust User Limit — hidden for Enterprise, show read-only summary instead */}
      {isEnterprise ? (
        <Card data-testid="enterprise-plan-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5 text-purple-600" />
              Enterprise Plan
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {enterprise?.max_users && !isUnlimited && (
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-sm text-slate-500">User Limit</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="enterprise-user-limit">
                    {enterprise.max_users.toLocaleString()} users
                  </p>
                </div>
              )}
              {isUnlimited && (
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-sm text-slate-500">User Limit</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="enterprise-user-limit">Unlimited</p>
                </div>
              )}
              {enterprise?.monthly_price != null && (
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-sm text-slate-500">Monthly Price</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="enterprise-monthly-price">
                    ${Number(enterprise.monthly_price).toLocaleString()}<span className="text-sm font-normal text-slate-500">/mo</span>
                  </p>
                </div>
              )}
            </div>
            <p className="text-sm text-slate-500 flex items-center gap-1.5">
              <Info className="h-4 w-4 shrink-0" />
              Enterprise pricing managed by your ProjectAI sales representative.
            </p>
          </CardContent>
        </Card>
      ) : (
      <Card data-testid="adjust-users-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Adjust User Limit</CardTitle>
          <CardDescription>
            Your tier and price per user automatically adjust based on user count.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Tier reference */}
          <div className="grid grid-cols-3 gap-3">
            {tiers.map(t => {
              // Highlight if any users fall into this tier band
              const isActive = costBreakdown.some(b => b.tier === t.display_name);
              return (
                <div
                  key={t.id}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    isActive
                      ? 'border-[#5AA2AE] bg-[#5AA2AE]/5 ring-1 ring-[#5AA2AE]'
                      : 'border-slate-200'
                  }`}
                >
                  <p className="font-semibold text-sm text-slate-900">{t.display_name}</p>
                  <p className="text-lg font-bold text-slate-700">${t.price_per_user}<span className="text-xs font-normal">/user</span></p>
                  <p className="text-xs text-slate-400">
                    {t.id === 'starter' ? `1–${t.max_users || 100}` : t.max_users ? `${(t.min_users || 0)+1}–${t.max_users}` : `${(t.min_users || 0)+1}+`} users
                  </p>
                </div>
              );
            })}
          </div>

          {/* Slider + Input */}
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <Button
                variant="outline" size="icon" className="h-8 w-8 shrink-0"
                onClick={() => setNewLimit(Math.max(currentUsers, newLimit - 10))}
                disabled={newLimit <= currentUsers}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Slider
                value={[newLimit]}
                onValueChange={([v]) => setNewLimit(v)}
                min={Math.max(1, currentUsers)}
                max={1000}
                step={1}
                className="flex-1"
                data-testid="user-limit-slider"
              />
              <Button
                variant="outline" size="icon" className="h-8 w-8 shrink-0"
                onClick={() => setNewLimit(Math.min(1000, newLimit + 10))}
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Input
                type="number"
                value={newLimit}
                onChange={(e) => setNewLimit(Math.max(currentUsers, Math.min(1000, parseInt(e.target.value) || 0)))}
                className="w-20 text-center"
                data-testid="user-limit-input"
              />
            </div>

            {/* Min users info */}
            {currentUsers > 0 && newLimit === currentUsers && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-700" data-testid="min-users-info">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  You currently have {currentUsers} invited users. Remove users from your projects to lower this limit further.
                </span>
              </div>
            )}

            {/* Summary - graduated breakdown */}
            <div className="p-3 bg-slate-50 rounded-lg space-y-2">
              {costBreakdown.map((b, i) => (
                <div key={i} className="flex items-center justify-between text-sm text-slate-600">
                  <span>{b.users} users &times; ${b.pricePerUser}/user <span className="text-slate-400">({b.tier})</span></span>
                  <span className="font-medium">${b.subtotal.toLocaleString()}</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                <div className="text-sm font-semibold text-slate-700">{newLimit} users total</div>
                <div className="text-lg font-bold text-slate-900" data-testid="monthly-cost">
                  ${monthlyCost.toLocaleString()}<span className="text-xs font-normal text-slate-500">/mo</span>
                </div>
              </div>
            </div>

            {/* Decrease warning */}
            {isDecrease && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-700">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  No refunds are offered for decreasing user counts in the current billing period.
                  The updated limit will take effect on your next billing cycle.
                </span>
              </div>
            )}

            {/* Pro-rata increase info */}
            {isIncrease && !isTrial && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-700" data-testid="prorata-info">
                <CreditCard className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Increasing from {paidUsers} to {newLimit} users requires a pro-rata payment
                  for the remaining days in the current billing period. You'll be redirected to Stripe to complete the payment.
                  Your new monthly cost will be <strong>${monthlyCost.toLocaleString()}/mo</strong>.
                </span>
              </div>
            )}

            <Button
              onClick={handleLimitChange}
              disabled={adjusting || newLimit === paidUsers || newLimit < 1}
              className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              data-testid="update-limit-btn"
            >
              {adjusting ? 'Updating...' : newLimit === paidUsers ? 'No Change' : 
                isDecrease ? 'Decrease User Limit' :
                !isTrial ? 'Pay Pro-Rata & Increase Limit' : 'Update User Limit'}
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:border-slate-300 transition-colors" onClick={fetchInvoices} data-testid="invoices-card">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">Invoices</p>
                <p className="text-sm text-slate-500">View payment history</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-slate-400" />
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-slate-300 transition-colors" onClick={async () => {
          try {
            const res = await fetch(`${API}/api/payments/billing-portal`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            const data = await res.json();
            if (data.error) {
              toast.error(data.error);
            } else if (data.url) {
              window.open(data.url, '_blank');
            }
          } catch (err) {
            toast.error('Unable to open billing portal. Please try again.');
          }
        }} data-testid="manage-billing-card">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">Manage Billing Details</p>
                <p className="text-sm text-slate-500">Payment methods & Stripe portal</p>
              </div>
            </div>
            <ExternalLink className="h-5 w-5 text-slate-400" />
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-slate-300 transition-colors" onClick={() => navigate('/pricing')} data-testid="billing-card">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Zap className="h-5 w-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">Pricing & Plans</p>
                <p className="text-sm text-slate-500">View pricing tiers</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-slate-400" />
          </CardContent>
        </Card>
      </div>

      {/* Cancel Subscription */}
      {!isTrial && subscription?.status === 'active' && (
        <Card className="border-red-100" data-testid="cancel-subscription-card">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div>
                <p className="font-medium text-slate-900">Cancel Subscription</p>
                <p className="text-sm text-slate-500">Your access continues until the end of the billing period</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => setShowCancel(true)}
              data-testid="cancel-subscription-btn"
            >
              Cancel Plan
            </Button>
          </CardContent>
        </Card>
      )}

      {subscription?.status === 'cancelled' && (
        <Card className="border-amber-200 bg-amber-50" data-testid="cancelled-status-card">
          <CardContent className="py-4 flex items-center gap-3">
            <Clock className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-900">Subscription Cancelled</p>
              <p className="text-sm text-amber-700">
                Your access continues until the end of the current billing period. You can resubscribe anytime from the Pricing page.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ========== DIALOGS ========== */}

      {/* Confirm Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmData?.is_decrease ? 'User Limit Decreased' : 'User Limit Updated'}
            </DialogTitle>
            <DialogDescription>{confirmData?.message}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">New Limit</span><span className="font-semibold">{confirmData?.new_limit} users</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Tier</span><span className="font-semibold">{confirmData?.tier}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Price per User</span><span className="font-semibold">${confirmData?.price_per_user}/mo</span></div>
            <div className="flex justify-between border-t pt-2"><span className="text-slate-500">Monthly Cost</span><span className="font-bold text-lg">${confirmData?.monthly_cost?.toLocaleString()}/mo</span></div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowConfirm(false)} data-testid="confirm-close-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invited Users Grid Dialog */}
      <Dialog open={showGrid} onOpenChange={setShowGrid}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Invited Users Across Projects</DialogTitle>
            <DialogDescription>
              {gridData?.total_unique_users || 0} unique users across {gridData?.projects?.length || 0} project(s)
            </DialogDescription>
          </DialogHeader>
          {gridData && (
            <div className="overflow-auto max-h-[60vh]">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-white z-10">
                  <tr>
                    <th className="text-left p-2 border-b font-medium text-slate-600 min-w-[200px]">Name</th>
                    <th className="text-left p-2 border-b font-medium text-slate-600 min-w-[200px]">Email</th>
                    {gridData.projects.map(p => (
                      <th key={p.id} className="text-center p-2 border-b font-medium text-slate-600 min-w-[100px]">
                        {p.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gridData.users.slice(0, 200).map((u, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="p-2 border-b border-slate-100 text-slate-900">{u.name}</td>
                      <td className="p-2 border-b border-slate-100 text-slate-500">{u.email}</td>
                      {gridData.projects.map(p => (
                        <td key={p.id} className="p-2 border-b border-slate-100 text-center">
                          {u.projects[p.id] ? (
                            <Check className="h-4 w-4 text-green-500 mx-auto" />
                          ) : (
                            <span className="text-slate-200">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {gridData.users.length > 200 && (
                <p className="text-xs text-slate-400 p-2">Showing first 200 of {gridData.users.length} users</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoices Dialog */}
      <Dialog open={showInvoices} onOpenChange={setShowInvoices}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Payment History</DialogTitle>
            <DialogDescription>All payments and invoices for your account.</DialogDescription>
          </DialogHeader>
          {invoiceData && (() => {
            const allTx = invoiceData.transactions || [];
            // Total Revenue = all transactions that were paid (including those later refunded)
            const totalRevenue = allTx
              .filter(tx => tx.payment_status === 'paid' || tx.payment_status === 'refunded')
              .reduce((sum, tx) => sum + (tx.amount || 0), 0);
            const totalRefunded = allTx
              .filter(tx => tx.payment_status === 'refunded')
              .reduce((sum, tx) => sum + (tx.refund_amount || tx.amount || 0), 0);
            const netRevenue = totalRevenue - totalRefunded;
            const hasTx = allTx.length > 0 || (invoiceData.invoices || []).length > 0;

            const fmtAmount = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

            return (
              <div className="space-y-4">
                {!hasTx ? (
                  <p className="text-slate-400 text-sm py-6 text-center">No payments yet.</p>
                ) : (
                  <>
                    {/* Transactions Table */}
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b">
                            <th className="text-left p-3 font-medium text-slate-600">Description</th>
                            <th className="text-right p-3 font-medium text-slate-600 w-[120px]">Amount</th>
                            <th className="text-center p-3 font-medium text-slate-600 w-[100px]">Status</th>
                            <th className="text-center p-3 font-medium text-slate-600 w-[50px]"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {allTx.map((tx, i) => (
                            <tr key={i} className="border-b last:border-b-0 hover:bg-slate-50/50" data-testid={`transaction-row-${i}`}>
                              <td className="p-3">
                                <p className="font-medium text-slate-900">
                                  {tx.package_id ? `${tx.package_id.charAt(0).toUpperCase() + tx.package_id.slice(1)} Plan` : 'Payment'}
                                </p>
                                <p className="text-xs text-slate-500">{new Date(tx.created_at).toLocaleDateString()}</p>
                                {tx.payment_status === 'refunded' && tx.refunded_at && (
                                  <p className="text-xs text-red-500">Refunded on {new Date(tx.refunded_at).toLocaleDateString()}</p>
                                )}
                              </td>
                              <td className="p-3 text-right">
                                <span className={`font-semibold ${tx.payment_status === 'refunded' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                                  {fmtAmount(tx.amount)}
                                </span>
                                {tx.payment_status === 'refunded' && tx.refund_amount > 0 && (
                                  <p className="text-red-600 font-semibold text-xs mt-0.5">-{fmtAmount(tx.refund_amount)}</p>
                                )}
                              </td>
                              <td className="p-3 text-center">
                                <Badge
                                  variant={tx.payment_status === 'paid' ? 'default' : 'secondary'}
                                  className={
                                    tx.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                                    tx.payment_status === 'refunded' ? 'bg-red-100 text-red-700' : ''
                                  }
                                >
                                  {tx.payment_status || tx.status}
                                </Badge>
                              </td>
                              <td className="p-3 text-center">
                                {tx.payment_status === 'paid' && tx.id && (
                                  <Button
                                    variant="ghost" size="sm"
                                    className="h-8 w-8 p-0 text-[#5AA2AE] hover:text-[#5AA2AE]/80"
                                    data-testid={`download-invoice-${i}`}
                                    onClick={async () => {
                                      try {
                                        const res = await fetch(`${API}/api/payments/invoice/${tx.id}`, {
                                          headers: { Authorization: `Bearer ${token}` },
                                        });
                                        if (!res.ok) throw new Error();
                                        const blob = await res.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `pulse-invoice-${tx.id.slice(0, 8)}.pdf`;
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                      } catch {
                                        toast.error('Failed to download invoice');
                                      }
                                    }}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                          {(invoiceData.invoices || []).map((inv, i) => (
                            <tr key={`inv-${i}`} className="border-b last:border-b-0 hover:bg-slate-50/50">
                              <td className="p-3">
                                <p className="font-medium text-slate-900">Invoice #{inv.id?.slice(0, 8)}</p>
                                <p className="text-xs text-slate-500">{inv.period_start} — {inv.period_end}</p>
                              </td>
                              <td className="p-3 text-right font-semibold text-slate-900">{fmtAmount(inv.amount)}</td>
                              <td className="p-3 text-center">
                                <Badge variant={inv.status === 'paid' ? 'default' : 'secondary'}
                                  className={inv.status === 'paid' ? 'bg-green-100 text-green-700' : ''}>
                                  {inv.status}
                                </Badge>
                              </td>
                              <td className="p-3"></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Upgrade from Trial Dialog */}
      <Dialog open={showUpgrade} onOpenChange={setShowUpgrade}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              Start Your Subscription
            </DialogTitle>
            <DialogDescription>
              Choose the number of users for your subscription plan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="p-4 bg-slate-50 border rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600">Current invited users</span>
                <span className="text-lg font-bold text-[#5AA2AE]">{currentUsers}</span>
              </div>
              <p className="text-xs text-slate-500">
                across {usage?.project_count || 0} project{usage?.project_count !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Subscribe for how many users?</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline" size="icon" className="h-8 w-8 shrink-0"
                  onClick={() => setUpgradeLimit(Math.max(currentUsers, upgradeLimit - 10))}
                  disabled={upgradeLimit <= currentUsers}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Slider
                  value={[upgradeLimit]}
                  onValueChange={([v]) => setUpgradeLimit(v)}
                  min={Math.max(1, currentUsers)}
                  max={1000}
                  step={1}
                  className="flex-1"
                  data-testid="upgrade-user-slider"
                />
                <Button
                  variant="outline" size="icon" className="h-8 w-8 shrink-0"
                  onClick={() => setUpgradeLimit(Math.min(1000, upgradeLimit + 10))}
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  value={upgradeLimit}
                  onChange={(e) => setUpgradeLimit(Math.max(currentUsers, Math.min(1000, parseInt(e.target.value) || 0)))}
                  className="w-20 text-center"
                  data-testid="upgrade-user-input"
                />
              </div>
              {upgradeLimit === currentUsers && currentUsers > 0 && (
                <p className="text-xs text-blue-600 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Starting at your current invited user count
                </p>
              )}
            </div>

            {/* Cost preview */}
            {(() => {
              const { cost, breakdown } = calcGraduatedCost(upgradeLimit);
              return (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-[#5AA2AE]/10 px-4 py-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Monthly Cost</span>
                    <span className="text-xl font-bold text-[#5AA2AE]">${cost.toLocaleString()}/mo</span>
                  </div>
                  {breakdown.length > 0 && (
                    <div className="px-4 py-2 space-y-1">
                      {breakdown.map((b, i) => (
                        <div key={i} className="flex justify-between text-xs text-slate-500">
                          <span>{b.users} users x ${b.pricePerUser}/user ({b.tier})</span>
                          <span>${b.subtotal.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowUpgrade(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpgradeCheckout}
              disabled={upgradeLoading || upgradeLimit < 1}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              data-testid="upgrade-checkout-btn"
            >
              {upgradeLoading ? 'Redirecting...' : `Subscribe — $${calcGraduatedCost(upgradeLimit).cost.toLocaleString()}/mo`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Cancel Subscription</DialogTitle>
            <DialogDescription>
              We're sorry to see you go. Your access will continue until the end of your current billing period.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <p className="font-medium mb-1">What happens when you cancel:</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li>Access continues until end of current billing period</li>
                <li>Your data will be preserved</li>
                <li>You can resubscribe at any time from the Pricing page</li>
                <li>After the billing period, your account reverts to the Free Trial tier</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cancel-reason">Why are you cancelling?</Label>
              <select
                id="cancel-reason"
                className="w-full border rounded-md p-2 text-sm"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                data-testid="cancel-reason-select"
              >
                <option value="">Select a reason...</option>
                <option value="too_expensive">Too expensive</option>
                <option value="not_using">Not using it enough</option>
                <option value="missing_features">Missing features I need</option>
                <option value="switching">Switching to another tool</option>
                <option value="temporary">Just need a break</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cancel-details">Additional feedback (optional)</Label>
              <textarea
                id="cancel-details"
                className="w-full border rounded-md p-2 text-sm resize-none"
                rows={3}
                placeholder="Help us improve..."
                value={cancelDetails}
                onChange={(e) => setCancelDetails(e.target.value)}
                data-testid="cancel-details-textarea"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCancel(false)} data-testid="cancel-keep-btn">
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              disabled={cancelling || !cancelReason}
              onClick={async () => {
                setCancelling(true);
                try {
                  const res = await fetch(`${API}/api/payments/cancel-subscription`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ reason: cancelReason, details: cancelDetails }),
                  });
                  const data = await res.json();
                  if (data.error) {
                    toast.error(data.error);
                  } else {
                    toast.success(data.message || 'Subscription cancelled');
                    setShowCancel(false);
                    fetchSubscription();
                  }
                } catch {
                  toast.error('Failed to cancel subscription');
                } finally {
                  setCancelling(false);
                }
              }}
              data-testid="cancel-confirm-btn"
            >
              {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
